import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  IconHeart,
  IconHeartFilled,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconMessageCircle,
} from "@tabler/icons-react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { openDeleteConfirm } from "../../../../redux/slices/uiSlice.js";
import useUserProfile from "../../../../hooks/useUserProfile";
import useContentOwnership from "../../../../hooks/useContentOwnership";
import AvatarById from "../../../../components/common/AvatarById";
import MentionAutocomplete from "../../../../components/MentionAutocomplete";
import { getDisplayName } from "../../../../utils/userDisplay";
import ActionButton from "../../../../components/common/ActionButton.jsx";
import MentionText from "../../../../components/common/MentionText";
import Button from "../../../../components/ui/Button";
import { formatRelativeTime } from "../../../../hooks/useRelativeTime";
import { injectStructuredMentions, stripStructuredMentions } from "../../../../utils/mentionInjection.js";

const EMPTY_OBJECT = Object.freeze({});

/** Maximum characters allowed in a reply — mirrors the limit in ReplyBox.js and Firestore rules. */
const REPLY_MAX_LENGTH = 2000;

// ─── Inline Reply Input ────────────────────────────────────────────────────────
/**
 * Minimal self-contained textarea used for reply-to-reply.
 * Separated from the top-level ReplyInput in ReplyBox so ReplyItem has no
 * dependency on its parent component.
 */
const InlineReplyInput = ({ onSubmit, isSubmitting, initialState = { text: "", mentionMap: {} } }) => {
  const [text, setText] = useState(initialState.text || "");
  const [mentionMap, setMentionMap] = useState(initialState.mentionMap || {});
  const inputRef = useRef(null);

  // Auto-focus when the inline input mounts and set cursor at end if initialState exists
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        if (initialState?.text) {
          try {
            const len = initialState.text.length;
            inputRef.current.setSelectionRange(len, len);
          } catch (err) {
            if (process.env.NODE_ENV !== "production") {
              console.error("[ReplyItem] Error setting cursor position:", err);
            }
          }
        }
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [initialState]);

  const handleMentionSelect = useCallback((user) => {
    if (!user || !user.username || !user.id) return;
    setMentionMap((prev) => ({ ...prev, [user.username]: user.id }));
  }, []);

  const handleSubmit = useCallback(() => {
    const trimmed = text.trim();
    if (trimmed && !isSubmitting) {
      const injectedText = injectStructuredMentions(trimmed, mentionMap);
      onSubmit(injectedText);
      setText("");
      setMentionMap({});
      if (inputRef.current) {
        try {
          inputRef.current.style.height = "auto";
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.error("[ReplyItem] Error resetting textarea height:", err);
          }
        }
      }
    }
  }, [text, isSubmitting, onSubmit, mentionMap]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const nearLimit = text.length >= REPLY_MAX_LENGTH * 0.9;
  const atLimit = text.length >= REPLY_MAX_LENGTH;

  return (
    <div className="reply-input">
      <MentionAutocomplete
        ref={inputRef}
        className="reply-textarea form-textarea"
        placeholder="Write a reply…"
        value={text}
        maxLength={REPLY_MAX_LENGTH}
        onChange={(newValue) => {
          setText(newValue);
        }}
        onKeyDown={handleKeyDown}
        onMentionSelect={handleMentionSelect}
        mentionNames={Object.keys(mentionMap)}
        rows={1}
        aria-label="Write a reply"
      />
      <div className="reply-input-bottom">
        <div className="char-counter-row">
          <span
            className={`char-counter${atLimit ? " at-limit" : nearLimit ? " near-limit" : ""}`}
          >
            {text.length}/{REPLY_MAX_LENGTH}
          </span>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={!text.trim() || isSubmitting}
          className="post-reply-button"
        >
          {isSubmitting ? "Posting…" : "Reply"}
        </Button>
      </div>
    </div>
  );
};

InlineReplyInput.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  initialState: PropTypes.shape({
    text: PropTypes.string,
    mentionMap: PropTypes.object,
  }),
};

// ─── ReplyItem ─────────────────────────────────────────────────────────────────
/**
 * Self-contained component for a single reply.
 *
 * Previously this was defined INSIDE ReplyBox, which caused React to treat it as
 * a brand-new component type on every ReplyBox render, unmounting and remounting
 * every reply instance on any state change (isSubmitting, showOptions, etc.).
 *
 * By defining it outside (and wrapping in React.memo), each ReplyItem only
 * re-renders when its own props change — ending the remount cascade.
 *
 * All per-item UI state (options menu, edit mode, inline reply) is managed
 * locally so parent state changes in ReplyBox do not affect sibling ReplyItems.
 */
const ReplyItem = React.memo(
  ({
    reply,
    commentId,
    postId,
    replyLikePending,
    isSubmitting,
    onEditSave,
    onLike,
    onReplySubmit,
  }) => {
    const dispatch = useDispatch();
    const currentUser = useSelector(
      (state) => state.auth.currentUser ?? EMPTY_OBJECT,
    );
    const userData = useSelector(
      (state) => state.auth.userData ?? EMPTY_OBJECT,
    );
    const isReplyLiked = useSelector(
      (state) => !!state.commentLikes?.likedReplyIds?.[reply.id],
    );

    // ── Per-item local UI state ──────────────────────────────────────────────
    const [showOptions, setShowOptions] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState("");
    const [editMentionMap, setEditMentionMap] = useState({});
    const [showInlineReply, setShowInlineReply] = useState(false);
    const optionsRef = useRef(null);

    // ── Author profile resolution ────────────────────────────────────────────
    const { profile } = useUserProfile(reply.userId);

    const display = useMemo(() => {
      const name = getDisplayName({
        profile,
        candidates: [reply.userName, reply.username],
      });
      const profession =
        profile?.userProfession ||
        profile?.profession ||
        reply.userProfession ||
        reply.profession ||
        "";
      const photoURL =
        profile?.photoURL ||
        profile?.userPhotoURL ||
        reply.photoURL ||
        reply.userPhotoURL ||
        "";
      return { name, profession, photoURL };
    }, [
      profile,
      reply.userName,
      reply.username,
      reply.userProfession,
      reply.profession,
      reply.photoURL,
      reply.userPhotoURL,
    ]);

    // ── Timestamp ────────────────────────────────────────────────────────────
    const timeLabel = useMemo(
      () => formatRelativeTime(reply?.timestamp),
      [reply?.timestamp],
    );

    // ── Ownership ────────────────────────────────────────────────────────────
    const { canManage } = useContentOwnership();

    // ── Click-outside / Escape for THIS item's options menu ──────────────────
    useEffect(() => {
      if (!showOptions) return;

      const handleClickOutside = (e) => {
        if (optionsRef.current && !optionsRef.current.contains(e.target)) {
          setShowOptions(false);
        }
      };
      const handleKeyDown = (e) => {
        if (e.key === "Escape") setShowOptions(false);
      };

      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [showOptions]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleEditStart = useCallback(() => {
      const { plainText, mentionMap: parsedMentions } = stripStructuredMentions(reply.text);
      setEditText(plainText);
      setEditMentionMap(parsedMentions);
      setIsEditing(true);
      setShowOptions(false);
    }, [reply.text]);

    const handleEditMentionSelect = useCallback((user) => {
      if (!user || !user.username || !user.id) return;
      setEditMentionMap((prev) => ({ ...prev, [user.username]: user.id }));
    }, []);

    const handleEditSave = useCallback(async () => {
      const trimmed = editText.trim();
      if (!trimmed) {
        setIsEditing(false);
        setEditText("");
        setEditMentionMap({});
        return;
      }
      const injectedText = injectStructuredMentions(trimmed, editMentionMap);
      const { plainText: storedPlain } = stripStructuredMentions(reply.text || "");
      if (trimmed === storedPlain.trim()) {
        setIsEditing(false);
        setEditText("");
        setEditMentionMap({});
        return;
      }
      await onEditSave(reply.id, injectedText);
      setIsEditing(false);
      setEditText("");
      setEditMentionMap({});
    }, [editText, reply.id, reply.text, editMentionMap, onEditSave]);

    const handleEditCancel = useCallback(() => {
      setIsEditing(false);
      setEditText("");
    }, []);

    const handleDelete = useCallback(() => {
      dispatch(
        openDeleteConfirm({
          itemId: reply.id,
          itemType: "reply",
          parentId: commentId,
          postId,
        }),
      );
      setShowOptions(false);
    }, [dispatch, reply.id, commentId, postId]);

    const handleInlineReplySubmit = useCallback(
      (text) => {
        // The inline reply input now injects its own structured tokens using
        // the provided initial state. No need to force-prepend here, which
        // would duplicate the mention or prevent the user from deleting it.
        onReplySubmit(text);
        setShowInlineReply(false);
      },
      [onReplySubmit],
    );

    const toggleInlineReply = useCallback(() => {
      setShowInlineReply((v) => !v);
    }, []);

    // XB4: Pre-populated @mention text for reply-to-reply input
    const inlineReplyInitialState = useMemo(() => {
      if (!display.name) return { text: "", mentionMap: {} };
      const userId = reply.userId || "unknown";
      return {
        text: `@${display.name} `,
        mentionMap: { [display.name]: userId },
      };
    }, [display.name, reply.userId]);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
      <div
        className={`reply-box${reply.isOptimistic ? " optimistic" : ""}`}
        data-reply-id={reply.id}
        aria-busy={reply.isOptimistic ? "true" : undefined}
      >
        {/* Header: avatar + name + timestamp + options */}
        <div className="reply-header">
          <div className="reply-user">
            <AvatarById
              userId={reply.userId}
              fallbackUsername={reply.username || "User"}
              size="medium"
              className="reply-avatar avatar-sm"
            />
            <div className="user-info">
              <h4>{display.name}</h4>
              <p className="reply-profession">{display.profession}</p>
            </div>
          </div>

          <div className="reply-header-right">
            <span className="reply-timestamp">
              {timeLabel || "just now"}
              {reply.updatedAt && (
                <span
                  className="edited-badge"
                  title={`Edited ${timeLabel || "recently"}`}
                >
                  {" · edited"}
                </span>
              )}
            </span>

            {canManage(reply) && !reply.isOptimistic && (
              <div className="reply-options" ref={optionsRef}>
                <button
                  className="options-toggle"
                  onClick={() => setShowOptions((v) => !v)}
                  aria-label="Reply options"
                  aria-haspopup="menu"
                  aria-expanded={showOptions}
                >
                  <IconDotsVertical size={16} />
                </button>

                {showOptions && (
                  <div
                    className="options-menu"
                    role="menu"
                    aria-label="Reply actions"
                  >
                    <button role="menuitem" onClick={handleEditStart}>
                      <IconEdit size={16} />
                      Edit
                    </button>
                    <button role="menuitem" onClick={handleDelete}>
                      <IconTrash size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Body: view mode or edit mode */}
        <div className="reply-content">
          {isEditing ? (
            <div className="reply-edit-container">
              <MentionAutocomplete
                value={editText}
                onChange={(newValue) => setEditText(newValue)}
                className="reply-edit-input form-textarea"
                maxLength={REPLY_MAX_LENGTH}
                placeholder="Edit reply…"
                aria-label="Edit reply"
                onMentionSelect={handleEditMentionSelect}
                mentionNames={Object.keys(editMentionMap)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleEditSave();
                  }
                  if (e.key === "Escape") handleEditCancel();
                }}
              />
              <div className="edit-buttons">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleEditSave}
                  disabled={!editText.trim()}
                >
                  Save
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleEditCancel}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <p>
              <MentionText text={reply.text} />
            </p>
          )}
        </div>

        <div className="reply-actions">
          <ActionButton
            onClick={() => onLike(reply.id)}
            icon={
              isReplyLiked ? (
                <IconHeartFilled size={16} color="#31C65B" />
              ) : (
                <IconHeart size={16} />
              )
            }
            text="Like"
            count={reply.likes || 0}
            active={isReplyLiked}
            disabled={!!replyLikePending[reply.id] || !!reply.isOptimistic}
            className="reply-like-btn"
            title={replyLikePending[reply.id] ? "Please wait…" : undefined}
          />
          {!reply.isOptimistic && (
            <ActionButton
              onClick={toggleInlineReply}
              icon={<IconMessageCircle size={16} />}
              text="Reply"
              active={showInlineReply}
              className={`reply-reply-btn${showInlineReply ? " active" : ""}`}
            />
          )}
        </div>

        {/* Inline reply-to-reply input */}
        {showInlineReply && (
          <div className="inline-reply-input">
            <InlineReplyInput
              onSubmit={handleInlineReplySubmit}
              isSubmitting={isSubmitting}
              initialState={inlineReplyInitialState}
            />
          </div>
        )}
      </div>
    );
  },
);

ReplyItem.displayName = "ReplyItem";

ReplyItem.propTypes = {
  reply: PropTypes.shape({
    id: PropTypes.string.isRequired,
    userId: PropTypes.string,
    username: PropTypes.string,
    userName: PropTypes.string,
    profession: PropTypes.string,
    userProfession: PropTypes.string,
    photoURL: PropTypes.string,
    userPhotoURL: PropTypes.string,
    text: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    likes: PropTypes.number,
    isLiked: PropTypes.bool,
    isOptimistic: PropTypes.bool,
  }).isRequired,
  commentId: PropTypes.string.isRequired,
  postId: PropTypes.string,
  replyLikePending: PropTypes.object,
  isSubmitting: PropTypes.bool,
  onEditSave: PropTypes.func.isRequired,
  onLike: PropTypes.func.isRequired,
  onReplySubmit: PropTypes.func.isRequired,
};

export default ReplyItem;
